import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/styles/webawesome.css';
import '../components/breadcrumb.js';
import '../components/empty_state.js';
import '../components/farm_import_preview.js';
import '../components/icon.js';

import { faSeedling } from '@fortawesome/free-solid-svg-icons';
import { css, html, LitElement, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import {
    type IFarmFacets,
    type IFarmImportResult,
    type IFarmImportRow,
    type IFarmListItem,
    importFarm,
    listAccessibleFarms,
} from '../libraries/api_client/farm.js';
import { loadFailed } from '../libraries/error_copy.js';
import { notifyFarmsChanged } from '../libraries/events.js';
import {
    farmFilterHeading,
    farmFilters,
    farmRestrictionQuery,
    farmSearch,
    noFarmsYetCopy,
} from '../libraries/farm_copy.js';
import {
    farmImportEntity,
    farmImportIntro,
    farmImportLoadingSkeleton,
    farmImportOutcomeNotices,
    summarizeFarmImport,
} from '../libraries/farm_import.js';
import { farmPath } from '../libraries/farm_paths.js';
import { type IImportDialogOptions, ImportDialog, importDialogHostStyles } from '../libraries/import_dialog.js';
import { legalFormLabel } from '../libraries/legal_form_options.js';
import { type IListFillActions, listEmptyCard, listFillButtons } from '../libraries/list_actions.js';
import {
    type IListResult,
    type IListState,
    ListController,
    listHostStyles,
    listResultFrom,
    restrictedPageResult,
} from '../libraries/list_controller.js';
import { listEmptyBody, showsEmptyCard } from '../libraries/list_empty_body.js';
import { type IListIdentity, listIds } from '../libraries/list_identity.js';
import type { IListFilter } from '../libraries/list_restriction.js';
import type { IListColumn } from '../libraries/list_table.js';
import { redirectTo } from '../libraries/navigation.js';
import { entityStatusBadge } from '../libraries/status_badge.js';

const farmPageSize = 25;

const farmColumns: Array<IListColumn<IFarmListItem>> = [
    {
        label: 'Name',
        track: 'm',
        cell: farm => farm.name,
    },
    {
        label: 'Rechtsform',
        cell: farm => legalFormLabel(farm.legalForm),
    },
    {
        label: 'Ort',
        track: 'xs',
        cell: farm => farm.city,
    },
    {
        label: 'Status',
        track: 'xs',
        cell: farm => entityStatusBadge(farm.active),
    },
];

/**
 * "Alle Betriebe" page rendered at `/farms` — the "Alle" scope.
 *
 * Lists every farm the caller can access one page at a time: orphan farms they
 * own plus every farm in every organisation they are a member of. Org-scoped
 * farms link into their canonical `/organizations/:orgId/farms/:farmId` URL;
 * orphan farms link to `/farms/:farmId`. Users with zero accessible farms see
 * the "Noch kein Betrieb angelegt" empty-state.
 *
 * „Importieren“ creates a farm from a backup (`docs/import_export.md` → *Betrieb aus
 * Sicherung*); it stands wherever „Betrieb anlegen“ stands, open to everyone who may
 * create a farm.
 */
@customElement('fm-farm-list')
export class FmFarmList extends LitElement {

    /** The preview's thirteen Düngejahr columns would scroll at the dialog's default width. */
    static styles = [
        listHostStyles,
        importDialogHostStyles,
        css`
            wa-dialog.import-dialog {
                --fm-wide-dialog-width: 80rem;
            }
        `,
    ];

    /** The import call, a property so the workshop and a test can answer it. */
    @property({
        attribute: false,
    })
    importFarm: typeof importFarm = importFarm;

    /**
     * The values the filters may narrow to, as the last load reported them. Held
     * here rather than read from the list, because the declaration is built from
     * it and a controller cannot hand a value to the thunk that declares it.
     */
    @state()
    private facets: IFarmFacets | undefined = undefined;

    private readonly list = new ListController<IFarmListItem, IFarmFacets | undefined>(this, {
        label: 'Betriebe',
        columns: (): Array<IListColumn<IFarmListItem>> => farmColumns,
        rowKey: (farm): string => farm.id,
        rowHref: (farm): string => this.farmHref(farm),
        withChevron: true,
        rowClass: (farm): string => (farm.active ? '' : 'row-inactive'),
        pageSize: farmPageSize,
        identity: (): IListIdentity => ({
            id: listIds.farms,
            scope: null,
        }),
        trackPageInUrl: true,
        readsDisplayFilter: true,
        search: farmSearch,
        filterHeading: farmFilterHeading,
        filters: (): ReadonlyArray<IListFilter> => farmFilters(this.facets),
        errorDescription: loadFailed('Die Betriebe', true),
        load: async ({
            offset, limit, hideInactive, search, filters, signal,
        }): Promise<IListResult<IFarmListItem, IFarmFacets | undefined>> => listResultFrom(
            await listAccessibleFarms({
                offset,
                limit,
                hideInactive,
                search,
                facets: this.facets === undefined,
                ...farmRestrictionQuery(filters),
            }, {
                signal,
            }),
            page => restrictedPageResult(page, page.items, page.facets)
        ),
        empty: (listState): TemplateResult => this.renderEmptyBody(listState),
        actions: (listState): Array<TemplateResult> => (
            showsEmptyCard(listState) ? [] : listFillButtons('actions', this.fill)
        ),
        onLoaded: ({ meta }): void => { this.facets = meta ?? this.facets; },
    });

    private readonly importDialog = new ImportDialog<IFarmImportRow, IFarmImportResult>(
        this,
        (): IImportDialogOptions<IFarmImportRow, IFarmImportResult> => ({
            label: 'Betrieb importieren',
            intro: farmImportIntro,
            accept: '.zip',
            entity: farmImportEntity,
            renderPreview: (result): TemplateResult => html`
                <fm-farm-import-preview .result=${result}></fm-farm-import-preview>
            `,
            renderLoading: farmImportLoadingSkeleton,
            preview: async (file, { signal }) => this.importFarm(file, 'preview', {
                signal,
            }),
            write: async file => this.importFarm(file, 'write'),
            summarize: summarizeFarmImport,
            submitLabel: 'Betrieb anlegen',
            onSuccess: async (result): Promise<void> => this.reportImport(result),
        })
    );

    /** The two ways to a new farm, open to every caller. */
    private readonly fill: IListFillActions = {
        create: {
            label: 'Betrieb anlegen',
            onClick: (): void => redirectTo('/farms/new'),
        },
        import: (): void => this.importDialog.stage(),
    };

    /**
     * Tells the shell a farm was added, for the switcher, and reloads the list under the
     * import's sentences.
     * @param result - What the write answered.
     */
    private async reportImport (result: IFarmImportResult): Promise<void> {
        notifyFarmsChanged(null);
        await this.list.confirmAllAndReload(farmImportOutcomeNotices(result));
    }

    private farmHref (farm: IFarmListItem): string {
        return farmPath(farm.managingOrganizationId, farm.id);
    }

    /**
     * The body while the table holds no rows, of which there are three, and the
     * display preference has to be tested first: `unfilteredTotal` counts the
     * same restriction minus the preference, so anything left there is a row the
     * restriction matched and the setting then hid, which makes the setting the
     * cause and the reset no help at all.
     * @param listState - What the list currently holds.
     * @returns The empty body.
     */
    private renderEmptyBody (listState: IListState<IFarmListItem>): TemplateResult {
        return listEmptyBody(listState, {
            hidden: 'Betriebe',
            resetRestriction: this.list.resetRestriction,
            empty: (): TemplateResult => listEmptyCard(faSeedling, noFarmsYetCopy, this.fill),
        });
    }

    render (): TemplateResult {
        return html`
            ${this.list.render(html`
                <fm-breadcrumb slot="breadcrumb" .items=${[
                    {
                        label: 'Alle',
                    },
                ]}></fm-breadcrumb>
            `)}
            ${this.importDialog.render()}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-farm-list': FmFarmList;
    }
}
